import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AppShell, PageHeader } from '@/components/AppShell';
import { ThemeSwitcher } from '@/components/ThemeSwitcher';
import { api } from '@/lib/api';
import { useApp } from '@/lib/store';
import { useT, LANGUAGES, useLang } from '@/lib/i18n';
import { ConfirmModal } from '@/components/ConfirmModal';
import { FamilyModeAdmin } from '@/components/FamilyModeAdmin';
import { APP_VERSION, SUPPORT_EMAIL, PH_HOTLINES } from '@/lib/version';
import { Palette, Crown, Shield, LogIn, LogOut, Check, Trash2, MapPin, Globe, Loader2, Info, FileText, Mail, Phone, ChevronRight, Heart, Home, Briefcase, Star, Pencil } from 'lucide-react';

export default function Account() {
  const t = useT();
  const navigate = useNavigate();
  const { lang, setLang } = useLang();
  const user = useApp((s) => s.user);
  const setUser = useApp((s) => s.setUser);
  const familyMode = useApp((s) => s.familyMode);
  const setConfig = useApp((s) => s.setConfig);
  const [plans, setPlans] = useState([]);
  const [places, setPlaces] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  const saveRename = async (id) => {
    const label = editName.trim();
    if (!label) { toast.error(t('fam_err')); return; }
    try {
      await api.renamePlace(id, label);
      setPlaces((prev) => prev.map((p) => (p.id === id ? { ...p, label } : p)));
      setEditingId(null);
      toast.success(t('fp_renamed'));
    } catch { toast.error(t('fam_err')); }
  };

  const removePlace = async (id) => {
    try { await api.deletePlace(id); setPlaces((prev) => prev.filter((p) => p.id !== id)); toast.success(t('fp_removed')); }
    catch { toast.error(t('fam_err')); }
  };
  const [busy, setBusy] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [adminTaps, setAdminTaps] = useState(0);
  const [showAdmin, setShowAdmin] = useState(false);

  useEffect(() => { api.plans().then((d) => setPlans(d.plans)).catch(() => {}); }, []);
  useEffect(() => { api.listPlaces().then(setPlaces).catch(() => {}); }, []);

  const signIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/app';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const signOut = async () => { try { await api.logout(); } catch (e) { console.debug('logout call failed (continuing to clear local state):', e); } setUser(null); toast.success(t('a_signed_out')); };

  const upgrade = async () => {
    if (!user) { toast.info(t('a_family_signin')); return; }
    setBusy(true);
    try { const res = await api.upgrade(); setUser(res.user); api.track('family_subscription_started'); toast.success(t('a_family_on')); }
    catch { toast.error('Could not upgrade.'); }
    finally { setBusy(false); }
  };

  const clearHistory = async () => {
    setClearOpen(false);
    try { const rides = await api.listRides(); await Promise.all(rides.map((r) => api.deleteRide(r.id).catch(() => {}))); toast.success(t('a_cleared')); }
    catch { toast.error('Could not clear history.'); }
  };

  const isFamily = user?.subscription_tier === 'family';

  return (
    <AppShell>
      <PageHeader title={t('a_title')} subtitle={t('a_sub')} />

      <div className="rounded-2xl p-4 border mb-4 flex items-center justify-between" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
        {user ? (
          <>
            <div className="flex items-center gap-3">
              {user.picture ? <img src={user.picture} alt="" className="w-11 h-11 rounded-full" /> : <span className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: 'var(--surface-alt)', color: 'var(--primary)' }}>{(user.display_name || 'U')[0]}</span>}
              <div><div className="font-bold" style={{ color: 'var(--text)' }}>{user.display_name}</div><div className="text-xs" style={{ color: 'var(--text-muted)' }}>{user.email}</div></div>
            </div>
            <button onClick={signOut} className="inline-flex items-center gap-1 text-sm font-semibold" style={{ color: 'var(--danger)' }} data-testid="sign-out-button"><LogOut size={16} /> {t('a_signout')}</button>
          </>
        ) : (
          <div className="w-full">
            <p className="font-bold" style={{ color: 'var(--text)' }}>{t('a_guest_t')}</p>
            <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>{t('a_guest_b')}</p>
            <button onClick={signIn} className="w-full inline-flex items-center justify-center gap-2 rounded-full font-bold min-h-[48px] border" style={{ borderColor: 'var(--border)', color: 'var(--text)', background: 'var(--surface)' }} data-testid="sign-in-google-button"><LogIn size={18} /> {t('a_signin')}</button>
          </div>
        )}
      </div>

      {/* Language */}
      <div className="rounded-2xl p-4 border mb-4" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid="language-section">
        <div className="flex items-center gap-2 mb-3"><Globe size={18} style={{ color: 'var(--primary)' }} /><h2 className="font-bold" style={{ color: 'var(--text)' }}>{t('a_language')}</h2></div>
        <div className="grid grid-cols-2 gap-2">
          {LANGUAGES.map((l) => {
            const active = lang === l.id;
            return (
              <button key={l.id} onClick={() => setLang(l.id)} data-testid={`account-language-${l.id}`} className="rounded-xl p-3 border text-left flex items-center justify-between" style={{ background: active ? 'var(--surface-alt)' : 'var(--surface)', borderColor: active ? 'var(--primary)' : 'var(--border)' }}>
                <span className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{l.label}</span>
                {active && <Check size={16} style={{ color: 'var(--primary)' }} />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Appearance */}
      <div className="rounded-2xl p-4 border mb-4" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid="appearance-section">
        <div className="flex items-center gap-2 mb-3"><Palette size={18} style={{ color: 'var(--primary)' }} /><h2 className="font-bold" style={{ color: 'var(--text)' }}>{t('a_appearance')}</h2></div>
        <p className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>{t('a_quickpick')}</p>
        <div className="mb-3"><ThemeSwitcher compact /></div>
        <p className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>{t('a_all_themes')}</p>
        <ThemeSwitcher onChange={(th) => { if (user) api.updateProfile({ theme_preference: th }).catch(() => {}); }} />
      </div>

      {/* Subscription */}
      {familyMode && (
      <div className="rounded-2xl p-4 border mb-4" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid="subscription-section">
        <div className="flex items-center gap-2 mb-3"><Crown size={18} style={{ color: 'var(--primary)' }} /><h2 className="font-bold" style={{ color: 'var(--text)' }}>{t('a_plan')}</h2></div>
        {plans.map((p) => {
          const current = (isFamily && p.id === 'family') || (!isFamily && p.id === 'free');
          return (
            <div key={p.id} className="rounded-xl p-3 border mb-2" style={{ borderColor: current ? 'var(--primary)' : 'var(--border)', background: current ? 'var(--surface-alt)' : 'var(--surface)' }}>
              <div className="flex items-center justify-between">
                <span className="font-bold" style={{ color: 'var(--text)' }}>{p.name}</span>
                <span className="font-extrabold" style={{ color: 'var(--text)' }}>{p.price_php === 0 ? t('a_free') : `₱${p.price_php}/mo`}</span>
              </div>
              <div className="mt-2 grid grid-cols-1 gap-1">
                {p.features.slice(0, p.id === 'family' ? 6 : 5).map((f) => (
                  <div key={f} className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--text-muted)' }}><Check size={14} style={{ color: 'var(--success)' }} /> {f}</div>
                ))}
              </div>
              {p.id === 'family' && !isFamily && (
                <button onClick={upgrade} disabled={busy} className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full font-bold min-h-[48px]" style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }} data-testid="upgrade-family-button">
                  {busy ? <Loader2 size={18} className="animate-spin" /> : <Crown size={18} />} {t('a_get_family')}
                </button>
              )}
              {current && <div className="mt-2 text-xs font-semibold" style={{ color: 'var(--primary)' }}>{t('a_current')}</div>}
            </div>
          );
        })}
        <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>{t('a_pay_note')}</p>
      </div>
      )}

      {/* Privacy */}
      <div className="rounded-2xl p-4 border mb-4" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid="privacy-section">
        <div className="flex items-center gap-2 mb-3"><Shield size={18} style={{ color: 'var(--primary)' }} /><h2 className="font-bold" style={{ color: 'var(--text)' }}>{t('a_privacy')}</h2></div>
        <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>{t('a_privacy_b')}</p>
        <div className="flex items-center gap-2 text-sm mb-3" style={{ color: 'var(--text)' }}><MapPin size={16} style={{ color: 'var(--text-muted)' }} /> {t('a_privacy_loc')}</div>
        <button onClick={() => setClearOpen(true)} className="w-full inline-flex items-center justify-center gap-2 rounded-full font-semibold min-h-[48px] border" style={{ borderColor: 'var(--danger)', color: 'var(--danger)', background: 'var(--surface)' }} data-testid="clear-history-button"><Trash2 size={18} /> {t('a_clear')}</button>
      </div>

      <ConfirmModal
        open={clearOpen}
        onClose={() => setClearOpen(false)}
        title={t('a_clear_q')}
        description={t('a_clear_b')}
        cancelLabel={t('cancel')}
        confirmLabel={t('a_clear_all')}
        onConfirm={clearHistory}
        danger
        confirmTestId="confirm-clear-history-button"
      />

      {/* Favourite places */}
      <div className="rounded-2xl p-4 border mb-4" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid="favourite-places-section">
        <div className="flex items-center gap-2 mb-1"><MapPin size={18} style={{ color: 'var(--primary)' }} /><h2 className="font-bold" style={{ color: 'var(--text)' }}>{t('fp_title')}</h2></div>
        <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>{t('fp_sub')}</p>
        {places.length === 0 ? (
          <p className="text-xs" style={{ color: 'var(--text-muted)' }} data-testid="favourite-places-empty">{t('fp_empty')}</p>
        ) : (
          <div className="space-y-2" data-testid="favourite-places-list">
            {places.map((p) => {
              const I = { home: Home, work: Briefcase, custom: Star }[p.kind] || Star;
              return (
                <div key={p.id} className="flex items-center gap-2 rounded-xl px-3 py-2 border" style={{ borderColor: 'var(--border)', background: 'var(--surface-alt)' }} data-testid="favourite-place-item">
                  <I size={16} style={{ color: 'var(--primary)', flexShrink: 0 }} />
                  <div className="min-w-0 flex-1">
                    {editingId === p.id ? (
                      <input
                        autoFocus
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') saveRename(p.id); if (e.key === 'Escape') setEditingId(null); }}
                        maxLength={40}
                        data-testid="place-rename-input"
                        className="w-full rounded-lg px-2 min-h-[36px] border text-sm outline-none"
                        style={{ background: 'var(--surface)', borderColor: 'var(--primary)', color: 'var(--text)' }}
                      />
                    ) : (
                      <>
                        <div className="text-sm font-semibold truncate" style={{ color: 'var(--text)' }}>{p.label}</div>
                        {p.address_text && <div className="text-[11px] truncate" style={{ color: 'var(--text-muted)' }}>{p.address_text}</div>}
                      </>
                    )}
                  </div>
                  {editingId === p.id ? (
                    <button onClick={() => saveRename(p.id)} aria-label={t('fp_save_custom')} className="w-9 h-9 rounded-full flex items-center justify-center" style={{ color: 'var(--success)' }} data-testid="place-rename-save"><Check size={17} /></button>
                  ) : (
                    <button onClick={() => { setEditingId(p.id); setEditName(p.label); }} aria-label={t('fp_rename')} className="w-9 h-9 rounded-full flex items-center justify-center" style={{ color: 'var(--text-muted)' }} data-testid="favourite-place-rename"><Pencil size={15} /></button>
                  )}
                  <button onClick={() => removePlace(p.id)} aria-label={t('fp_remove')} className="w-9 h-9 rounded-full flex items-center justify-center" style={{ color: 'var(--danger)' }} data-testid="favourite-place-remove"><Trash2 size={16} /></button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* About & Legal */}
      <div className="rounded-2xl p-4 border mb-4" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid="about-legal-section">
        <div className="flex items-center gap-2 mb-3"><Info size={18} style={{ color: 'var(--primary)' }} /><h2 className="font-bold" style={{ color: 'var(--text)' }}>{t('a_about_legal')}</h2></div>
        <div className="flex flex-col">
          {[
            { label: t('foot_story'), icon: Heart, to: '/about', testid: 'account-about-link' },
            { label: t('foot_privacy'), icon: Shield, to: '/privacy', testid: 'account-privacy-link' },
            { label: t('foot_terms'), icon: FileText, to: '/terms', testid: 'account-terms-link' },
          ].map((row) => (
            <button key={row.to} onClick={() => navigate(row.to)} data-testid={row.testid} className="flex items-center justify-between py-3 border-b last:border-b-0" style={{ borderColor: 'var(--border)' }}>
              <span className="inline-flex items-center gap-2 text-sm font-semibold" style={{ color: 'var(--text)' }}><row.icon size={16} style={{ color: 'var(--text-muted)' }} /> {row.label}</span>
              <ChevronRight size={16} style={{ color: 'var(--text-muted)' }} />
            </button>
          ))}
          <a href={`mailto:${SUPPORT_EMAIL}`} data-testid="account-support-email" className="flex items-center justify-between py-3 border-b" style={{ borderColor: 'var(--border)' }}>
            <span className="inline-flex items-center gap-2 text-sm font-semibold" style={{ color: 'var(--text)' }}><Mail size={16} style={{ color: 'var(--text-muted)' }} /> {t('a_contact')}</span>
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{SUPPORT_EMAIL}</span>
          </a>
        </div>

        <div className="mt-3 rounded-xl p-3" style={{ background: 'var(--surface-alt)' }}>
          <div className="text-xs font-bold mb-2" style={{ color: 'var(--text)' }}>{t('a_hotlines')}</div>
          <div className="flex flex-col gap-1.5">
            {PH_HOTLINES.map((h) => (
              <a key={h.number} href={`tel:${h.number.replace(/\s/g, '')}`} className="flex items-center justify-between text-sm" data-testid="account-hotline" style={{ color: 'var(--text)' }}>
                <span className="inline-flex items-center gap-1.5"><Phone size={13} style={{ color: 'var(--danger)' }} /> {h.label}</span>
                <span className="font-bold tabular-nums" style={{ color: 'var(--danger)' }}>{h.number}</span>
              </a>
            ))}
          </div>
        </div>

        <p
          className="text-[11px] text-center mt-3 select-none cursor-default"
          style={{ color: 'var(--text-muted)' }}
          data-testid="account-version"
          onClick={() => { const n = adminTaps + 1; setAdminTaps(n); if (n >= 5) setShowAdmin(true); }}
        >KilalaRide v{APP_VERSION}</p>
      </div>

      {(showAdmin || user?.is_admin) && (
        <div className="rounded-2xl p-4 border mb-4" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid="account-admin-section">
          <FamilyModeAdmin />
        </div>
      )}

      <p className="text-xs text-center" style={{ color: 'var(--text-muted)' }}>{t('disclaimer')}</p>
    </AppShell>
  );
}
